import {
  CheckIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import React, { MouseEvent, ReactNode, useEffect, useRef, useState } from "react";

type CopyStatus = "idle" | "copied" | "error";

type CopyIdentifierButtonProps = {
  value: string | number | null | undefined;
  description: string;
  className?: string;
  wrapperClassName?: string;
};

type CopyableIdentifierProps = CopyIdentifierButtonProps & {
  displayValue?: ReactNode;
  emptyDisplay?: ReactNode;
  valueClassName?: string;
};

const STATUS_DURATION_MS = 2000;

const classNames = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

const sentenceCase = (value: string) =>
  value.length > 0 ? `${value[0].toUpperCase()}${value.slice(1)}` : value;

/** Copies an operational identifier without triggering its surrounding row. */
export function CopyIdentifierButton({
  value,
  description,
  className,
  wrapperClassName,
}: CopyIdentifierButtonProps) {
  const [status, setStatus] = useState<CopyStatus>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyValue = value == null ? "" : String(value);
  const hasValue = copyValue.trim().length > 0;

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    [],
  );

  if (!hasValue) return null;

  const setTemporaryStatus = (nextStatus: CopyStatus) => {
    setStatus(nextStatus);
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setStatus("idle"), STATUS_DURATION_MS);
  };

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard API unavailable");
      }
      await navigator.clipboard.writeText(copyValue);
      setTemporaryStatus("copied");
    } catch {
      setTemporaryStatus("error");
    }
  };

  const statusMessage =
    status === "copied"
      ? `${sentenceCase(description)} copied`
      : status === "error"
        ? `Could not copy ${description}`
        : "";

  return (
    <span className={classNames("inline-flex shrink-0", wrapperClassName ?? "relative")}>
      <button
        type="button"
        aria-label={`Copy ${description}`}
        title={`Copy ${description}`}
        className={classNames(
          "inline-flex h-6 w-6 items-center justify-center rounded text-slate-500 transition-colors hover:text-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500",
          status === "error" ? "text-red-600" : "",
          className,
        )}
        onClick={handleCopy}
      >
        {status === "copied" ? (
          <CheckIcon className="h-3.5 w-3.5 text-indigo-600" aria-hidden="true" />
        ) : status === "error" ? (
          <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <DocumentDuplicateIcon className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>
      {status !== "idle" ? (
        <span
          role="status"
          aria-live="polite"
          className={classNames(
            "pointer-events-none absolute bottom-full right-0 z-50 mb-1 whitespace-nowrap rounded px-2 py-1 text-xs text-white shadow-sm",
            status === "error" ? "bg-red-600" : "bg-slate-800",
          )}
        >
          {statusMessage}
        </span>
      ) : null}
    </span>
  );
}

export default function CopyableIdentifier({
  value,
  displayValue,
  emptyDisplay = "—",
  description,
  className,
  valueClassName,
}: CopyableIdentifierProps) {
  const copyValue = value == null ? "" : String(value);
  const hasValue = copyValue.trim().length > 0;

  if (!hasValue) {
    return <span className={valueClassName}>{displayValue ?? emptyDisplay}</span>;
  }

  return (
    <span className={classNames("relative inline-block min-w-0 pr-5", className)}>
      <span className={classNames("block break-all", valueClassName)}>
        {displayValue ?? copyValue}
      </span>
      <CopyIdentifierButton
        value={copyValue}
        description={description}
        wrapperClassName="absolute -right-1 -top-1.5"
      />
    </span>
  );
}
